import cn from 'classnames';
// Types
import { UserRole } from 'api/graphql-global-types';
import { SportLandingConfig } from 'helpers/sportLandings';
// Helpers
import {
  buildImageFetchSrcSet,
  withCDN,
  withImageFetchIfRemote,
} from 'helpers/cdn';
import { formatCurrencyString } from 'helpers/formatCurrencyString';
import { generateLandingPageStructuredData } from 'helpers/structured-data';
// Hooks
import { useGetCurrUser, useJoinAsTalent } from 'hooks';
// Layouts
import Head from 'layouts/Head/Head';
import Header from 'layouts/Header/Header';
import Main from 'layouts/Main/Main';
import Footer from 'layouts/Footer/Footer';
// Ui
import Button from 'ui3/Button/Button';
import Image from 'ui3/Image/Image';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
// Components
import Feature from './Feature/Feature';
// Styles
import styles from './SportLanding.module.scss';

const HERO_WIDTHS = [640, 1024, 1440];
const HERO_SIZES = '100vw';

const MERCH_COLORS = ['white', 'black', 'grey', 'gold'];

const FAN_COMMENTS = [
  {
    avatar: withCDN('/images/recruitment/fan_1.png'),
    text: 'Oh, man! This is awesome. Best fight ever!',
  },
  {
    avatar: withCDN('/images/recruitment/fan_2.png'),
    text: 'Dude! Have you seen that!!??',
  },
  {
    avatar: withCDN('/images/recruitment/fan_3.png'),
    text: 'That was mad! 🥊😬',
  },
];

type SportLandingProps = {
  config: SportLandingConfig;
};

const SportLanding = ({ config }: SportLandingProps) => {
  const joinAsTalent = useJoinAsTalent();
  const { data: currentUser } = useGetCurrUser();
  const isTalent =
    !!currentUser?.me?.id && currentUser.me.role !== UserRole.Customer;
  const ctaLabel = isTalent ? 'Go to your dashboard' : 'Set up your account';
  const heroSrcSet = buildImageFetchSrcSet(config.hero.image, HERO_WIDTHS);

  return (
    <>
      <Head
        title={config.seo.title}
        description={config.seo.description}
        image={config.hero.image}
        imageAlt={config.hero.imageAlt}
        imagePreload={
          heroSrcSet ? { srcSet: heroSrcSet, sizes: HERO_SIZES } : undefined
        }
        structuredData={generateLandingPageStructuredData({
          name: config.seo.schemaName,
          description: config.seo.description,
          path: config.path,
          faq: config.faq,
        })}
      />
      <Header />

      <Main withoutSidesPadding withoutBottomPadding>
        <section className={styles.hero}>
          <Image
            src={config.hero.image}
            alt={config.hero.imageAlt}
            className={styles.heroImage}
            ssrFill
            ssrWidths={HERO_WIDTHS}
            sizes={HERO_SIZES}
            priority
          />
          <div className={styles.heroContent}>
            <h1 className={styles.heroTitle}>{config.hero.title}</h1>
            <p className={styles.heroSubtitle}>{config.hero.subtitle}</p>
            <Button onClick={joinAsTalent}>{ctaLabel}</Button>
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{config.howItWorks.title}</h2>
          <p className={styles.sectionText}>{config.howItWorks.intro}</p>
          <ol className={styles.steps}>
            {config.howItWorks.steps.map((step, index) => (
              <li key={step.title}>
                <span className={styles.stepNumber} aria-hidden="true">
                  {index + 1}
                </span>
                <h3 className={styles.itemTitle}>{step.title}</h3>
                <p className={styles.itemText}>{step.text}</p>
              </li>
            ))}
          </ol>
        </section>

        <Feature
          feature={config.personalVideos}
          ctaLabel={ctaLabel}
          onCtaClick={joinAsTalent}
        />

        <Feature
          feature={config.stream}
          ctaLabel={ctaLabel}
          onCtaClick={joinAsTalent}
          reversed
        >
          <ul className={styles.comments} aria-label="Fan comments">
            {FAN_COMMENTS.map((comment) => (
              <li key={comment.avatar} className={styles.comment}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={withImageFetchIfRemote(comment.avatar, 64)}
                  width={32}
                  height={32}
                  alt=""
                  className={styles.commentAvatar}
                  loading="lazy"
                />
                <span>{comment.text}</span>
              </li>
            ))}
          </ul>
        </Feature>

        <Feature
          feature={config.broadcast}
          ctaLabel={ctaLabel}
          onCtaClick={joinAsTalent}
        />

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{config.merch.title}</h2>
          <p className={styles.sectionText}>{config.merch.text}</p>
          <ul className={styles.merch}>
            {config.merch.products.map((product) => (
              <li key={product.name} className={styles.merchItem}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={withImageFetchIfRemote(product.image, 640)}
                  width={400}
                  height={400}
                  alt={product.name}
                  className={styles.merchImage}
                  loading="lazy"
                  decoding="async"
                />
                <span className={styles.itemTitle}>{product.name}</span>
                <span className={styles.merchAthlete}>{product.athlete}</span>
                <div className={styles.merchFooter}>
                  <span className={styles.merchColors} aria-label="Colors">
                    {MERCH_COLORS.map((color) => (
                      <span
                        key={color}
                        className={cn(styles.merchColor, styles[color])}
                      />
                    ))}
                  </span>
                  <span className={styles.merchPrice}>
                    {formatCurrencyString(product.price)}
                  </span>
                  <button
                    type="button"
                    className={styles.merchCart}
                    aria-label={`Add ${product.name} to cart`}
                    disabled
                  >
                    <MillionsIcon name="shoppingCart" size={20} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{config.onePageLink.title}</h2>
          <p className={styles.sectionText}>{config.onePageLink.text}</p>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={withCDN('/images/recruitment/linked_social_networks.svg')}
            width={1029}
            height={618}
            alt="Social networks linked to a MILLIONS page"
            className={styles.onePageImage}
            loading="lazy"
            decoding="async"
          />
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Frequently asked questions</h2>
          <div className={styles.faq}>
            {config.faq.map((item) => (
              <details key={item.question} className={styles.faqItem}>
                <summary className={styles.faqQuestion}>
                  <h3>{item.question}</h3>
                  <MillionsIcon
                    name="chevronDown"
                    size={24}
                    className={styles.faqIcon}
                  />
                </summary>
                <p className={styles.faqAnswer}>{item.answer}</p>
              </details>
            ))}
          </div>
        </section>
      </Main>

      <Footer />
    </>
  );
};

export default SportLanding;
